import { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { marketplaceApi } from '../lib/api';
import { formatCurrency, formatRelativeTime } from '../lib/utils';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Briefcase, Building2, Users, Clock, DollarSign, ChevronRight, Send, Loader2, CheckCircle, XCircle, Filter } from 'lucide-react';

const CATEGORY_COLORS = {
  tech: 'bg-blue-500/20 text-blue-500',
  lifestyle: 'bg-pink-500/20 text-pink-500',
  fintech: 'bg-green-500/20 text-green-500',
  sports: 'bg-orange-500/20 text-orange-500',
  food: 'bg-yellow-500/20 text-yellow-500',
  finance: 'bg-emerald-500/20 text-emerald-500',
  music: 'bg-purple-500/20 text-purple-500',
};

const MarketplacePage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('briefs');
  const [briefs, setBriefs] = useState([]);
  const [myApplications, setMyApplications] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [applyingTo, setApplyingTo] = useState(null);
  const [pitch, setPitch] = useState('');
  const [proposedRate, setProposedRate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [briefsRes, appsRes, catsRes] = await Promise.all([
        marketplaceApi.getBriefs(selectedCategory),
        marketplaceApi.getMyApplications(),
        marketplaceApi.getCategories(),
      ]);
      setBriefs(briefsRes.data);
      setMyApplications(appsRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      toast.error('Failed to load marketplace data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApply = async (briefId) => {
    if (!pitch.trim()) { toast.error('Please write a pitch'); return; }
    if (!proposedRate || parseFloat(proposedRate) <= 0) { toast.error('Please enter your proposed rate'); return; }
    setIsSubmitting(true);
    try {
      await marketplaceApi.apply(briefId, pitch, parseFloat(proposedRate));
      toast.success('Application submitted!');
      setApplyingTo(null);
      setPitch('');
      setProposedRate('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      pending: 'bg-yellow-500/20 text-yellow-500',
      shortlisted: 'bg-blue-500/20 text-blue-500',
      accepted: 'bg-green-500/20 text-green-500',
      rejected: 'bg-red-500/20 text-red-500',
    };
    return map[status] || 'bg-white/10 text-muted-foreground';
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="p-6 lg:p-0 space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-48 w-full rounded-xl" /><Skeleton className="h-64 w-full rounded-xl" /></div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen lg:min-h-0 pb-24 lg:pb-0">
        {/* Header */}
        <div className="p-6 lg:p-0 lg:mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Briefcase className="w-7 h-7 inline mr-2 text-primary" />Creator Marketplace
          </h1>
          <p className="text-muted-foreground">Find brand deals and grow your earnings</p>
        </div>

        <div className="lg:grid lg:grid-cols-3 lg:gap-6">
          <div className="lg:col-span-2">
            {/* Stats */}
            <div className="px-6 lg:px-0 mb-6 grid grid-cols-3 gap-3">
              <div className="bg-card border border-white/10 rounded-xl p-4">
                <Briefcase className="w-5 h-5 text-primary mb-2" />
                <p className="text-xs text-muted-foreground">Open Briefs</p>
                <p className="text-xl font-bold">{briefs.filter(b => b.status === 'open').length}</p>
              </div>
              <div className="bg-card border border-white/10 rounded-xl p-4">
                <Send className="w-5 h-5 text-blue-500 mb-2" />
                <p className="text-xs text-muted-foreground">My Applications</p>
                <p className="text-xl font-bold">{myApplications.length}</p>
              </div>
              <div className="bg-card border border-white/10 rounded-xl p-4">
                <DollarSign className="w-5 h-5 text-green-500 mb-2" />
                <p className="text-xs text-muted-foreground">Total Budget</p>
                <p className="text-xl font-bold">{formatCurrency(briefs.reduce((s, b) => s + b.budget, 0))}</p>
              </div>
            </div>

            <div className="px-6 lg:px-0">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="briefs">Brand Briefs</TabsTrigger>
                  <TabsTrigger value="applications">My Applications</TabsTrigger>
                </TabsList>

                <TabsContent value="briefs" className="space-y-4">
                  {/* Category Filter */}
                  <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2">
                    <button onClick={() => { setSelectedCategory(null); fetchData(); }} className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap ${!selectedCategory ? 'bg-primary text-white' : 'bg-white/5 text-muted-foreground hover:text-white'}`}>All</button>
                    {categories.map((cat) => (
                      <button key={cat.category} onClick={() => { setSelectedCategory(cat.category); }} className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap capitalize ${selectedCategory === cat.category ? 'bg-primary text-white' : 'bg-white/5 text-muted-foreground hover:text-white'}`}>
                        {cat.category} ({cat.count})
                      </button>
                    ))}
                  </div>

                  {briefs.filter(b => !selectedCategory || b.category === selectedCategory).map((brief) => (
                    <div key={brief.brief_id} className="bg-card border border-white/10 rounded-xl p-5" data-testid={`brief-${brief.brief_id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center"><Building2 className="w-6 h-6 text-primary" /></div>
                          <div>
                            <h3 className="font-semibold">{brief.title}</h3>
                            <p className="text-sm text-muted-foreground">{brief.brand_name}</p>
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full capitalize ${CATEGORY_COLORS[brief.category] || 'bg-white/10 text-muted-foreground'}`}>{brief.category}</span>
                      </div>

                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{brief.description}</p>

                      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mb-4">
                        <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /><span className="text-foreground font-medium">{formatCurrency(brief.budget)}</span> budget</span>
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{brief.slots_filled}/{brief.slots_total} slots filled</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Due {new Date(brief.deadline).toLocaleDateString()}</span>
                        <span>{brief.applications_count} applications</span>
                      </div>

                      {brief.has_applied ? (
                        <div className="flex items-center gap-2 p-3 bg-green-500/10 rounded-xl">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <span className="text-sm text-green-500 font-medium">Applied - {brief.my_application?.status}</span>
                        </div>
                      ) : applyingTo === brief.brief_id ? (
                        <div className="space-y-3 p-4 bg-white/5 rounded-xl">
                          <textarea value={pitch} onChange={(e) => setPitch(e.target.value)} placeholder="Write your pitch... Why should this brand work with you?" className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-sm focus:border-primary focus:ring-0 focus:outline-none min-h-[80px]" data-testid="pitch-input" />
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">Your rate:</span>
                            <span className="text-sm">₦</span>
                            <Input type="number" value={proposedRate} onChange={(e) => setProposedRate(e.target.value)} placeholder="100,000" className="bg-white/5 border-white/10 flex-1" data-testid="rate-input" />
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={() => handleApply(brief.brief_id)} disabled={isSubmitting} className="flex-1 bg-primary hover:bg-red-600" data-testid="submit-application">
                              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Submit
                            </Button>
                            <Button variant="outline" onClick={() => setApplyingTo(null)}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <Button onClick={() => setApplyingTo(brief.brief_id)} className="w-full bg-primary hover:bg-red-600" data-testid={`apply-${brief.brief_id}`}>
                          Apply Now<ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      )}
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="applications" className="space-y-3">
                  {myApplications.length === 0 ? (
                    <div className="text-center py-12 bg-card border border-white/10 rounded-xl"><Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><h3 className="font-semibold mb-2">No applications yet</h3><p className="text-muted-foreground text-sm">Browse brand briefs and start applying!</p></div>
                  ) : myApplications.map((app) => (
                    <div key={app.application_id} className="bg-card border border-white/10 rounded-xl p-4" data-testid={`application-${app.application_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-medium">{app.brief_title || 'Brand Brief'}</p>
                          <p className="text-sm text-muted-foreground">{app.brand_name}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusBadge(app.status)}`}>{app.status}</span>
                      </div>
                      <p className="text-sm text-muted-foreground truncate mb-2">"{app.pitch?.slice(0, 80)}..."</p>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Proposed: {formatCurrency(app.proposed_rate)}</span>
                        <span>{formatRelativeTime(app.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="lg:col-span-1 mt-6 lg:mt-0 px-6 lg:px-0 space-y-6">
            {/* Top Categories */}
            <div className="bg-card border border-white/10 rounded-xl p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2"><Filter className="w-5 h-5 text-primary" />Categories</h3>
              <div className="space-y-2">
                {categories.map((cat) => (
                  <button key={cat.category} onClick={() => { setSelectedCategory(cat.category); setActiveTab('briefs'); }} className="w-full flex items-center justify-between p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-colors text-left">
                    <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${CATEGORY_COLORS[cat.category] || 'bg-white/10 text-muted-foreground'}`}>{cat.category}</span>
                    <span className="text-sm text-muted-foreground">{cat.count} briefs</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Tips for Creators */}
            <div className="bg-card border border-white/10 rounded-xl p-5">
              <h3 className="font-semibold mb-4">Tips for Success</h3>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>1. Write a compelling pitch that shows your unique angle</p>
                <p>2. Propose a fair rate based on your audience size</p>
                <p>3. Highlight past brand collaborations</p>
                <p>4. Respond quickly to brand messages</p>
                <p>5. Deliver high-quality content on time</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default MarketplacePage;
